import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ShieldAlert, Check, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import { createChildLinkFlow } from "../lib/childLinkFlow.mjs";

/**
 * Global modal for the CHILD side of the parent-child handshake.
 *
 * Polls /api/child/pending-link on mount, on focus, and every 45 s.
 * When a pending link exists that already has parent_confirmed=true, we
 * explain exactly what confirming grants. Closing is not consent or a
 * server-side decline. A nonblocking review button reopens the request.
 *
 * Once the child confirms and the server flips the link to `linked`, the
 * modal auto-dismisses. Declining also dismisses (server marks 'declined').
 */
export default function ChildLinkModal() {
  const { user, refresh } = useAuth();
  const userId = user?.user_id;
  const [state, setState] = useState(null);
  const flowRef = useRef(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!userId) return;
    const flow = createChildLinkFlow({
      userId, api, notify: toast,
      refresh: () => refreshRef.current(),
      storage: {
        getItem: key => sessionStorage.getItem(key),
        setItem: (key, value) => sessionStorage.setItem(key, value),
        removeItem: key => sessionStorage.removeItem(key),
      },
      onChange: next => setState({ ...next, userId }),
    });
    flowRef.current = flow;
    flow.load();
    const t = setInterval(() => flow.load(), 45000);
    const onFocus = () => flow.load();
    window.addEventListener("focus", onFocus);
    return () => {
      flow.dispose();
      flowRef.current = null;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId]);

  const current = state?.userId === userId && userId ? state : null;
  const pending = current?.pending;
  const visible = !!pending && !current.dismissed;
  const closeRef = useRef(null);
  useEffect(() => {
    if (!visible) return;
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const onKey = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        flowRef.current?.dismiss();
      }
      if (event.key === "Tab") {
        const buttons = Array.from(document.querySelectorAll(
          '[data-testid="child-link-modal"] button:not(:disabled)'
        ));
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [visible]);

  if (!pending) return null;
  if (current.dismissed) {
    return (
      <button data-testid="child-link-review" onClick={() => flowRef.current?.review()}
        className="fixed bottom-24 right-4 z-[90] rounded-full bg-zinc-900 border border-white/20 px-4 py-2 text-sm text-zinc-100">
        Review parental link request
      </button>
    );
  }
  const busy = current.busy || current.loading;

  const parentName = pending.other?.name || pending.other?.email || "A parent";

  const confirm = () => flowRef.current?.confirm();
  const decline = () => flowRef.current?.decline();

  return (
    <div
      data-testid="child-link-modal"
      data-back-dismiss='[data-testid="child-link-close"]'
      role="dialog"
      aria-modal="true"
      aria-labelledby="child-link-title"
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm grid place-items-center p-6"
    >
      <div className="w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-3xl border border-[#FFCC00]/40 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="size-11 rounded-full bg-[#FFCC00]/15 grid place-items-center">
            <ShieldAlert className="text-[#FFCC00]" size={22}/>
          </div>
          <div className="label-eyebrow">Parental Link Request</div>
          <button ref={closeRef} data-testid="child-link-close"
            onClick={() => flowRef.current?.dismiss()} aria-label="Close link request"
            className="ml-auto p-2 text-zinc-300 hover:text-white"><X size={20}/></button>
        </div>

        <h2 id="child-link-title" className="font-display font-black text-2xl tracking-tighter mt-4">
          Confirm Child Account link
        </h2>
        <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
          You are receiving an invitation to link your account as a{" "}
          <span className="font-bold">Child Account</span> under{" "}
          <span className="font-bold text-[#FFCC00]" data-testid="child-link-parent-name">{parentName}</span>.
          Confirming this link will allow {parentName} to access location tracking and parental controls, including:
        </p>

        <ul className="mt-4 space-y-2 text-sm text-zinc-400">
          <li className="flex gap-2"><MapPin size={14} className="text-[#007AFF] mt-0.5"/> Live location + history for the linked child.</li>
          <li className="flex gap-2"><ShieldAlert size={14} className="text-[#FFCC00] mt-0.5"/> Safe-zone geofences & entry/exit alerts.</li>
          <li className="flex gap-2"><Check size={14} className="text-[#34C759] mt-0.5"/> Check-in reception and Find-Me requests.</li>
        </ul>

        <div className="mt-5 p-3 rounded-xl border border-white/10 bg-white/[0.03] text-[11px] text-zinc-500 leading-relaxed">
          You cannot unlink this yourself once accepted — only your parent may remove the link.
          Decline instead if you did not expect this invitation.
        </div>

        {current.error && (
          <p role="alert" data-testid="child-link-error" className="mt-4 text-sm text-red-300">
            {current.error}
          </p>
        )}
        <p className="mt-3 text-xs text-zinc-400">
          Closing leaves the request undecided. You can review it later.
          {current.busy && " Closing cannot undo a request already processed by the server."}
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <button
            data-testid="child-link-decline"
            disabled={busy}
            onClick={decline}
            className="py-3 rounded-full border border-white/10 hover:bg-white/5 text-zinc-300 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <X size={16}/> {current.busy === "decline" ? "Declining…" : "Decline"}
          </button>
          <button
            data-testid="child-link-confirm"
            disabled={busy}
            onClick={confirm}
            className="py-3 rounded-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Check size={16}/> {current.busy === "confirm" ? "Confirming…" : "Confirm Connection"}
          </button>
        </div>
      </div>
    </div>
  );
}
